import React, { useEffect, useState } from 'react'
import { BellOutlined, LoginOutlined, LogoutOutlined } from '@ant-design/icons'
import {
	Button,
	Card,
	Col,
	Divider,
	InputNumber,
	Row,
	Space,
	Spin,
	Switch,
	Typography,
	message,
} from 'antd'
import { httpGet, httpPut } from '../../common/http.service'

const { Title, Text, Paragraph } = Typography

export type MobileSegmentReminder = {
	/** null = disabled */
	minutesBeforeShift: number | null
	atExactShiftStart: boolean
	/** null = disabled */
	minutesAfterShift: number | null
}

type UiSegment = {
	beforeEnabled: boolean
	beforeMinutes: number
	atExact: boolean
	afterEnabled: boolean
	afterMinutes: number
}

const defaultUi = (): UiSegment => ({
	beforeEnabled: true,
	beforeMinutes: 15,
	atExact: false,
	afterEnabled: false,
	afterMinutes: 5,
})

const apiToUi = (raw: Partial<MobileSegmentReminder> | undefined | null): UiSegment => {
	if (!raw || typeof raw !== 'object') return defaultUi()
	const before =
		typeof raw.minutesBeforeShift === 'number' && Number.isFinite(raw.minutesBeforeShift)
			? raw.minutesBeforeShift
			: null
	const after =
		typeof raw.minutesAfterShift === 'number' && Number.isFinite(raw.minutesAfterShift)
			? raw.minutesAfterShift
			: null
	return {
		beforeEnabled: before != null,
		beforeMinutes: before != null ? Math.round(before) : 15,
		atExact: raw.atExactShiftStart === true,
		afterEnabled: after != null,
		afterMinutes: after != null ? Math.round(after) : 5,
	}
}

const uiToApi = (u: UiSegment): MobileSegmentReminder => ({
	minutesBeforeShift: u.beforeEnabled ? Math.round(u.beforeMinutes) : null,
	atExactShiftStart: u.atExact,
	minutesAfterShift: u.afterEnabled ? Math.round(u.afterMinutes) : null,
})

const ShiftReminderTab: React.FC = () => {
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [checkIn, setCheckIn] = useState<UiSegment>(defaultUi)
	const [checkOut, setCheckOut] = useState<UiSegment>(defaultUi)

	const load = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			const d = response?.data ?? {}
			setCheckIn(apiToUi(d.checkInReminder))
			setCheckOut(apiToUi(d.checkOutReminder))
		} catch (e) {
			console.error('Failed to load app settings:', e)
			message.error('Failed to load shift reminder settings')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		load()
	}, [])

	const validateSegment = (label: string, u: UiSegment): boolean => {
		if (u.beforeEnabled) {
			const v = Number(u.beforeMinutes)
			if (!Number.isFinite(v) || v < 1 || v > 180) {
				message.error(`${label}: minutes before shift must be 1–180 when enabled`)
				return false
			}
		}
		if (u.afterEnabled) {
			const v = Number(u.afterMinutes)
			if (!Number.isFinite(v) || v < 0 || v > 180) {
				message.error(`${label}: minutes after shift must be 0–180 when enabled`)
				return false
			}
		}
		if (!u.beforeEnabled && !u.atExact && !u.afterEnabled) {
			message.error(`${label}: enable at least one reminder (before, at start, or after)`)
			return false
		}
		return true
	}

	const handleSave = async () => {
		if (!validateSegment('Check-in reminder', checkIn)) return
		if (!validateSegment('Check-out reminder', checkOut)) return
		try {
			setSaving(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			const current = response?.data ?? {}
			await httpPut('/api/v1/admin/app-settings', {
				...current,
				checkInReminder: uiToApi(checkIn),
				checkOutReminder: uiToApi(checkOut),
			})
			message.success('Shift reminder settings saved')
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Failed to save settings')
		} finally {
			setSaving(false)
		}
	}

	const renderSegmentCard = (
		title: string,
		icon: React.ReactNode,
		value: UiSegment,
		setValue: React.Dispatch<React.SetStateAction<UiSegment>>,
		anchorLabel: string,
	) => (
		<Card
			title={
				<span>
					{icon}
					<span style={{ marginLeft: 8 }}>{title}</span>
				</span>
			}
			bordered={false}
			style={{ marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
		>
			<Paragraph type='secondary' style={{ marginBottom: 16 }}>
				Anchored to <Text strong>{anchorLabel}</Text>. The mobile app schedules local
				notifications for today when the user opens the app (or after check-in/out). If the
				staff already completed that activity for the segment, matching reminders are not
				scheduled again until the next shift activity.
			</Paragraph>

			<Row align='middle' justify='space-between' style={{ padding: '10px 0' }}>
				<Col flex='1'>
					<Title level={5} style={{ margin: 0 }}>
						Minutes before {anchorLabel}
					</Title>
					<Text type='secondary'>1–180 minutes before the anchor time (disabled if off).</Text>
				</Col>
				<Col>
					<Space>
						<Switch
							checked={value.beforeEnabled}
							onChange={(checked) =>
								setValue((p) => ({ ...p, beforeEnabled: checked }))
							}
						/>
						<InputNumber
							min={1}
							max={180}
							disabled={!value.beforeEnabled}
							value={value.beforeMinutes}
							onChange={(v) =>
								setValue((p) => ({ ...p, beforeMinutes: Number(v ?? 15) }))
							}
						/>
					</Space>
				</Col>
			</Row>

			<Divider style={{ margin: '12px 0' }} />

			<Row align='middle' justify='space-between' style={{ padding: '10px 0' }}>
				<Col flex='1'>
					<Title level={5} style={{ margin: 0 }}>
						At exact {anchorLabel}
					</Title>
					<Text type='secondary'>One reminder exactly when the anchor time is reached.</Text>
				</Col>
				<Col>
					<Switch
						checked={value.atExact}
						onChange={(checked) => setValue((p) => ({ ...p, atExact: checked }))}
					/>
				</Col>
			</Row>

			<Divider style={{ margin: '12px 0' }} />

			<Row align='middle' justify='space-between' style={{ padding: '10px 0' }}>
				<Col flex='1'>
					<Title level={5} style={{ margin: 0 }}>
						Minutes after {anchorLabel}
					</Title>
					<Text type='secondary'>0–180 minutes after the anchor (disabled if off).</Text>
				</Col>
				<Col>
					<Space>
						<Switch
							checked={value.afterEnabled}
							onChange={(checked) =>
								setValue((p) => ({ ...p, afterEnabled: checked }))
							}
						/>
						<InputNumber
							min={0}
							max={180}
							disabled={!value.afterEnabled}
							value={value.afterMinutes}
							onChange={(v) =>
								setValue((p) => ({ ...p, afterMinutes: Number(v ?? 0) }))
							}
						/>
					</Space>
				</Col>
			</Row>
		</Card>
	)

	return (
		<div>
			<Spin spinning={loading}>
				<Card
					title={
						<span>
							<BellOutlined style={{ marginRight: 8 }} />
							Shift reminders (mobile)
						</span>
					}
					bordered={false}
					style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
				>
					<Paragraph type='secondary' style={{ marginBottom: 0 }}>
						Configure local notifications on the staff mobile app. Check-in reminders use
						shift <Text strong>start</Text> times; check-out reminders use shift{' '}
						<Text strong>end</Text> times (including second shift when configured). Stored
						in Firestore with other app settings.
					</Paragraph>
				</Card>

				{renderSegmentCard(
					'Check-in reminder',
					<LoginOutlined />,
					checkIn,
					setCheckIn,
					'shift start',
				)}
				{renderSegmentCard(
					'Check-out reminder',
					<LogoutOutlined />,
					checkOut,
					setCheckOut,
					'shift end',
				)}

				<Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 20 }}>
					<Button type='primary' onClick={handleSave} loading={saving}>
						Save
					</Button>
				</Space>
			</Spin>
		</div>
	)
}

export default ShiftReminderTab
